import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { KaliaCalendarConfig, KaliaEvent, KaliaResource } from './types';
import { DEFAULT_CONFIG } from './types';
import { useKaliaCalendarLayout } from './useKaliaCalendarLayout';
import KaliaCalendarHeader from './KaliaCalendarHeader';
import KaliaCalendarTimeColumn from './KaliaCalendarTimeColumn';
import KaliaCalendarColumn from './KaliaCalendarColumn';
import KaliaCalendarNowIndicator from './KaliaCalendarNowIndicator';

const TIME_COL_WIDTH = 56;

type Props = {
    resources: KaliaResource[];
    events: KaliaEvent[];
    config?: Partial<KaliaCalendarConfig>;
};

function KaliaCalendar({ resources, events, config: configOverrides }: Props) {
    const config = useMemo<KaliaCalendarConfig>(
        () => ({ ...DEFAULT_CONFIG, ...configOverrides }),
        [configOverrides]
    );

    const sortedResources = useMemo(
        () => [...resources].sort((a, b) => a.order - b.order),
        [resources]
    );

    const { dayStartMinutes, dayEndMinutes, totalSlots, totalHeight, timeSlots, eventsByResource } =
        useKaliaCalendarLayout(sortedResources, events, config);

    const scrollRef = useRef<HTMLDivElement>(null);

    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const columnWidth = useMemo(() => {
        const count = sortedResources.length;
        if (count === 0) return config.minColumnWidth;
        const available = containerWidth - TIME_COL_WIDTH;
        if (available <= 0) return config.minColumnWidth;
        const stretched = Math.floor(available / count);
        return Math.max(config.minColumnWidth, stretched);
    }, [containerWidth, sortedResources.length, config.minColumnWidth]);

    const gridContentWidth = sortedResources.length * columnWidth + TIME_COL_WIDTH;

    return (
        <div
            className='flex flex-col h-full border border-gray-300 rounded-md bg-white overflow-hidden'
            style={{ width: 0, minWidth: '100%' }}
        >
            {/* Scroll container */}
            <div ref={scrollRef} className='flex-1 overflow-auto relative'>
                {/* Header row - sticky */}
                <KaliaCalendarHeader
                    resources={sortedResources}
                    columnWidth={columnWidth}
                />

                {/* Body: time column + resource columns */}
                <div className='relative flex' style={{ minWidth: gridContentWidth }}>
                    {/* Time column - sticky left */}
                    <KaliaCalendarTimeColumn timeSlots={timeSlots} config={config} />

                    {/* Resource columns */}
                    <div className='flex relative'>
                        {/* Now indicator spanning all columns */}
                        <KaliaCalendarNowIndicator
                            dayStartMinutes={dayStartMinutes}
                            dayEndMinutes={dayEndMinutes}
                            slotDuration={config.slotDuration}
                            slotHeight={config.slotHeight}
                            timezone={config.timezone}
                            totalWidth={sortedResources.length * columnWidth}
                        />

                        {sortedResources.map((resource) => {
                            const data = eventsByResource.get(resource.id);
                            return (
                                <KaliaCalendarColumn
                                    key={resource.id}
                                    resource={resource}
                                    foregroundEvents={data?.foreground || []}
                                    backgroundEvents={data?.background || []}
                                    config={config}
                                    columnWidth={columnWidth}
                                    totalHeight={totalHeight}
                                    totalSlots={totalSlots}
                                    dayStartMinutes={dayStartMinutes}
                                    allResources={sortedResources}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(KaliaCalendar);
